import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

/**
 * T.Z122 — the root route's `errorElement`: the LAST line of defence between a
 * render error and the white screen. The «ПДн» crash of 2026-09-17 (a
 * `TypeError` out of the mask engine) unmounted the whole SPA because React
 * had no boundary above the root route; from now on ANY render error anywhere
 * in the tree lands on this card instead.
 *
 * Scope note (on purpose, not a gap): a React error boundary catches RENDER
 * errors, not errors thrown from event handlers or timers — the mask engine
 * guards those itself (fail-open, `./demo/demo-mask.ts`). This component only
 * promises that a failing ROUTE never blanks the app.
 *
 * Deliberately frugal: nothing here may itself throw. The error is read once,
 * logged content-free, and never re-dereferenced; the only interaction is a
 * FULL reload (re-rendering the just-failed route client-side would restart
 * the loop that brought us here). The card reuses the signed-out interstitial
 * layout (`theme.css`) — no new CSS for an emergency screen.
 */
export function RouteErrorFallback() {
  const error = useRouteError();

  useEffect(() => {
    // One diagnostic line, content-free: an error object can carry PII (a
    // masked ФИО read back, a request body) — the class of failure is enough.
    console.error('[web] render error caught by the root errorElement:', error);
  }, [error]);

  const status = isRouteErrorResponse(error) ? error.status : undefined;

  return (
    <div className="interstitial" role="alert" data-testid="route-error-fallback">
      <div className="interstitial__card">
        <span className="interstitial__logo" aria-hidden="true">
          !
        </span>
        <h1>Что-то пошло не так</h1>
        <p>Обновите страницу.</p>
        {status !== undefined ? <p>Код ошибки: {status}.</p> : null}
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => window.location.reload()}
          data-testid="route-error-reload"
        >
          Перезагрузить
        </button>
      </div>
    </div>
  );
}
